import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Calendar, 
  Users, 
  Award, 
  Clock, 
  Phone, 
  MapPin, 
  Star,
  ArrowRight,
  Heart,
  Shield,
  Activity,
  Stethoscope,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { Card, Button, Badge } from '../../components/ui';
import { mockDoctors, mockServices, mockTestimonials, mockClinicInfo } from '../../data/mockData';
import { formatCurrency } from '../../utils';

const HomePage: React.FC = () => {
  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  // Auto-rotate testimonials
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTestimonial((prev) => 
        prev === mockTestimonials.length - 1 ? 0 : prev + 1
      );
    }, 5000);

    return () => clearInterval(timer);
  }, []);

  const featuredServices = mockServices.slice(0, 4);
  const featuredDoctors = mockDoctors.slice(0, 3);

  const stats = [
    { label: 'Patients Served', value: '10,000+', icon: Users },
    { label: 'Years of Experience', value: '20+', icon: Award },
    { label: 'Medical Professionals', value: '50+', icon: Stethoscope },
    { label: 'Success Rate', value: '98%', icon: Activity },
  ];

  const features = [
    {
      icon: Heart,
      title: 'Compassionate Care',
      description: 'We provide personalized, empathetic healthcare with your well-being as our top priority.'
    },
    {
      icon: Shield,
      title: 'Advanced Technology',
      description: 'State-of-the-art medical equipment and innovative treatment approaches for better outcomes.'
    },
    {
      icon: Clock,
      title: '24/7 Emergency Care',
      description: 'Round-the-clock emergency services with immediate response for critical medical situations.'
    },
    {
      icon: Users,
      title: 'Expert Team',
      description: 'Board-certified physicians and specialists dedicated to providing exceptional medical care.'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-screen bg-gradient-to-br from-slate-800 via-blue-900 to-blue-800 text-white overflow-hidden">
        {/* Medical Background Image */}
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1551601651-2a8555f1a136?w=1920&h=1080&fit=crop&crop=center"
            alt="Modern Medical Facility"
            className="w-full h-full object-cover opacity-60"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-slate-800/60 via-blue-900/70 to-blue-800/65"></div>
        </div>
        
        {/* Decorative background elements */}
        <div className="absolute inset-0">
          <div className="absolute top-0 right-0 w-96 h-96 bg-blue-500 rounded-full opacity-10 blur-3xl transform translate-x-32 -translate-y-32"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-teal-500 rounded-full opacity-10 blur-3xl transform -translate-x-32 translate-y-32"></div>
        </div>
        
        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="text-center space-y-8">
            {/* Clinic Badge */}
            <div className="inline-flex items-center px-6 py-3 bg-white/10 backdrop-blur-sm rounded-full border border-white/20">
              <span className="text-sm font-medium text-white/90">
                ClinicIT Medical Center
              </span>
            </div>
            
            {/* Main Heading */}
              <div className="space-y-4">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight tracking-tight">
                Medical Care{' '}
                <span className="bg-gradient-to-r from-teal-400 to-cyan-400 bg-clip-text text-transparent">
                  EASY
                </span>{' '}
                and Secure
                </h1>
              <p className="text-lg md:text-xl text-blue-100 max-w-3xl mx-auto leading-relaxed">
                Get your medical care handled quickly and efficiently with our expert consultation services
                </p>
              </div>
              
            {/* CTA Button */}
            <div className="pt-6">
                <Link to="/booking">
                  <Button 
                    size="lg" 
                  className="bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 text-white font-semibold px-8 py-3 rounded-full shadow-xl transform hover:scale-105 transition-all duration-300"
                  >
                  Start My Consultation
                  </Button>
                </Link>
              </div>

            {/* Contact Info */}
            <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-8 pt-12">
              <div className="flex items-center space-x-2 text-blue-100">
                <Phone className="w-5 h-5" />
                <span>{mockClinicInfo.phone}</span>
              </div>
              <div className="flex items-center space-x-2 text-blue-100">
                <MapPin className="w-5 h-5" />
                <span>{mockClinicInfo.address.city}, {mockClinicInfo.address.state}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-gradient-to-br from-slate-50 to-blue-50 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-0 w-72 h-72 bg-teal-200 rounded-full opacity-20 blur-3xl -translate-x-36 -translate-y-36"></div>
          <div className="absolute bottom-0 right-0 w-72 h-72 bg-blue-200 rounded-full opacity-20 blur-3xl translate-x-36 translate-y-36"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Trusted by Thousands
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Our commitment to excellence is reflected in our numbers
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="group">
                <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100">
                  <div className="text-center">
                    <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-2xl mb-6 shadow-lg">
                      <stat.icon className="w-10 h-10 text-white" />
                    </div>
                    <div className="text-4xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent mb-3">{stat.value}</div>
                    <div className="text-gray-600 font-medium">{stat.label}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 bg-white relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <div className="inline-flex items-center px-4 py-2 bg-teal-50 rounded-full mb-6">
              <span className="text-sm font-semibold text-teal-700">Why Choose Us</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Excellence in{' '}
              <span className="bg-gradient-to-r from-teal-500 to-cyan-500 bg-clip-text text-transparent">
                Healthcare
              </span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              We combine medical expertise with compassionate care to provide you with 
              the best healthcare experience possible.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="group">
                <div className="relative bg-gradient-to-br from-white to-gray-50 rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-3 border border-gray-100 overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-br from-teal-500/5 to-cyan-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                  <div className="relative text-center">
                    <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-2xl mb-6 shadow-lg group-hover:scale-110 transition-transform duration-300">
                      <feature.icon className="w-10 h-10 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-4 group-hover:text-teal-600 transition-colors duration-300">{feature.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 bg-gradient-to-br from-gray-50 to-slate-100 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-1/4 right-0 w-96 h-96 bg-gradient-to-l from-teal-200 to-cyan-200 rounded-full opacity-30 blur-3xl translate-x-48"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <div className="inline-flex items-center px-4 py-2 bg-cyan-50 rounded-full mb-6">
              <span className="text-sm font-semibold text-cyan-700">Our Services</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Comprehensive{' '}
              <span className="bg-gradient-to-r from-teal-500 to-cyan-500 bg-clip-text text-transparent">
                Healthcare
              </span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Comprehensive healthcare services delivered by our team of experienced medical professionals.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
            {featuredServices.map((service, index) => (
              <div key={service.id} className="group">
                <div className="relative bg-white rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 border border-gray-100 overflow-hidden">
                  <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-bl from-teal-400 to-cyan-400 opacity-10 rounded-bl-3xl"></div>
                  <div className="relative">
                    <div className="inline-flex items-center justify-center w-18 h-18 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-2xl mb-6 shadow-lg group-hover:scale-110 transition-transform duration-300">
                      <Stethoscope className="w-9 h-9 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-teal-600 transition-colors duration-300">{service.name}</h3>
                    <p className="text-gray-600 text-sm leading-relaxed mb-6">{service.description}</p>
                    <div className="space-y-3">
                      <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-teal-500 to-cyan-500 text-white font-semibold rounded-full text-sm shadow-lg">
                        {formatCurrency(service.price)}
                      </div>
                      <p className="text-xs text-gray-500 font-medium">{service.duration} minutes session</p>
                  </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center">
            <Link to="/services">
              <Button size="lg" className="bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 text-white font-semibold px-8 py-4 rounded-full shadow-xl transform hover:scale-105 transition-all duration-300">
                View All Services
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Doctors Section */}
      <section className="py-24 bg-white relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-0 w-96 h-96 bg-gradient-to-r from-blue-200 to-indigo-200 rounded-full opacity-20 blur-3xl -translate-x-48 -translate-y-48"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <div className="inline-flex items-center px-4 py-2 bg-blue-50 rounded-full mb-6">
              <span className="text-sm font-semibold text-blue-700">Our Medical Team</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Expert{' '}
              <span className="bg-gradient-to-r from-teal-500 to-cyan-500 bg-clip-text text-transparent">
                Physicians
              </span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Our team of board-certified physicians and specialists are dedicated to providing 
              exceptional medical care.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 mb-16">
            {featuredDoctors.map((doctor) => (
              <div key={doctor.id} className="group">
                <div className="relative bg-gradient-to-br from-white to-gray-50 rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-3 border border-gray-100 overflow-hidden">
                  <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-bl from-teal-400 to-cyan-400 opacity-10 rounded-bl-3xl"></div>
                  <div className="relative text-center">
                    <div className="relative mb-8">
                <img
                  src={doctor.photo}
                  alt={doctor.name}
                        className="w-36 h-36 rounded-full object-cover mx-auto border-4 border-white shadow-xl group-hover:scale-105 transition-transform duration-300"
                      />
                      <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-teal-500 to-cyan-500 text-white px-4 py-1 rounded-full text-xs font-semibold shadow-lg">
                        {doctor.specialty}
                      </div>
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-teal-600 transition-colors duration-300">{doctor.name}</h3>
                <div className="flex items-center justify-center space-x-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                          className={`w-5 h-5 ${
                        i < Math.floor(doctor.rating) ? 'text-yellow-400 fill-current' : 'text-gray-300'
                      }`}
                    />
                  ))}
                      <span className="text-sm text-gray-600 ml-2 font-medium">
                        {doctor.rating} ({doctor.reviewCount})
                  </span>
                </div>
                    <p className="text-gray-600 leading-relaxed mb-8">{doctor.bio}</p>
                <Link to={`/doctors/${doctor.id}`}>
                      <Button className="w-full bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 text-white font-semibold py-3 rounded-xl shadow-lg transform hover:scale-105 transition-all duration-300">
                    View Profile
                  </Button>
                </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center">
            <Link to="/doctors">
              <Button size="lg" className="bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 text-white font-semibold px-8 py-4 rounded-full shadow-xl transform hover:scale-105 transition-all duration-300">
                Meet All Doctors
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              What Our Patients Say
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Read testimonials from our satisfied patients who have experienced 
              our exceptional healthcare services.
            </p>
          </div>

          <div className="relative max-w-4xl mx-auto">
            <Card className="text-center" padding="lg">
              <div className="flex items-center justify-center space-x-1 mb-6">
                {[...Array(5)].map((_, i) => (
                  <Star
                    key={i}
                    className={`w-6 h-6 ${
                      i < mockTestimonials[currentTestimonial].rating
                        ? 'text-yellow-400 fill-current'
                        : 'text-gray-300'
                    }`}
                  />
                ))}
              </div>
              
              <blockquote className="text-xl text-gray-700 mb-8 leading-relaxed">
                "{mockTestimonials[currentTestimonial].comment}"
              </blockquote>
              
              <div className="flex items-center justify-center space-x-4">
                <div>
                  <p className="font-semibold text-gray-900">
                    {mockTestimonials[currentTestimonial].patientName}
                  </p>
                  <p className="text-sm text-gray-600">
                    Verified Patient
                  </p>
                </div>
              </div>
            </Card>

            {/* Navigation buttons */}
            <button
              onClick={() => setCurrentTestimonial(prev => 
                prev === 0 ? mockTestimonials.length - 1 : prev - 1
              )}
              className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-12 p-2 bg-white rounded-full shadow-lg hover:bg-gray-50 transition-colors"
            >
              <ChevronLeft className="w-6 h-6 text-gray-600" />
            </button>
            
            <button
              onClick={() => setCurrentTestimonial(prev => 
                prev === mockTestimonials.length - 1 ? 0 : prev + 1
              )}
              className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-12 p-2 bg-white rounded-full shadow-lg hover:bg-gray-50 transition-colors"
            >
              <ChevronRight className="w-6 h-6 text-gray-600" />
            </button>

            {/* Dots indicator */}
            <div className="flex justify-center space-x-2 mt-8">
              {mockTestimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentTestimonial(index)}
                  className={`w-3 h-3 rounded-full transition-colors ${
                    index === currentTestimonial ? 'bg-blue-600' : 'bg-gray-300'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative bg-gradient-to-br from-slate-800 via-blue-900 to-blue-800 text-white overflow-hidden">
        {/* Medical Background Image */}
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1576091160399-112ba8d25d1f?w=1920&h=1080&fit=crop&crop=center"
            alt="Medical Technology"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-slate-800/80 via-blue-900/85 to-blue-800/80"></div>
        </div>

        {/* Decorative background elements */}
        <div className="absolute inset-0">
          <div className="absolute top-0 left-0 w-96 h-96 bg-teal-500 rounded-full opacity-10 blur-3xl transform -translate-x-32 -translate-y-32"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-cyan-500 rounded-full opacity-10 blur-3xl transform translate-x-32 translate-y-32"></div>
        </div>

        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <div className="space-y-6">
            {/* Section Badge */}
            <div className="inline-flex items-center px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full border border-white/20">
              <span className="text-sm font-medium text-white/90">
            Ready to Get Started?
              </span>
            </div>

            {/* Main Heading */}
            <div className="space-y-4">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold leading-tight tracking-tight">
                Take the First Step to{' '}
                <span className="bg-gradient-to-r from-teal-400 to-cyan-400 bg-clip-text text-transparent">
                  Better Health
                </span>
          </h2>
              <p className="text-lg md:text-xl text-blue-100 max-w-3xl mx-auto leading-relaxed">
                Book your appointment today and experience the ClinicIT difference with our expert medical professionals.
          </p>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center pt-6">
            <Link to="/booking">
                <Button size="lg" className="bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 text-white font-semibold px-8 py-3 rounded-full shadow-xl transform hover:scale-105 transition-all duration-300">
                <Calendar className="w-5 h-5 mr-2" />
                Book Appointment Now
              </Button>
            </Link>
            <Link to="/contact">
                <Button variant="outline" size="lg" className="border-2 border-white text-white hover:bg-white hover:text-slate-800 backdrop-blur-sm font-semibold px-8 py-3 rounded-full shadow-lg transform hover:scale-105 transition-all duration-300">
                <Phone className="w-5 h-5 mr-2" />
                Contact Us
              </Button>
            </Link>
            </div>

            {/* Additional Info */}
            <div className="flex flex-col sm:flex-row items-center justify-center space-y-3 sm:space-y-0 sm:space-x-8 pt-8">
              <div className="flex items-center space-x-2 text-blue-100">
                <div className="w-2 h-2 bg-teal-400 rounded-full"></div>
                <span className="text-sm font-medium">24/7 Emergency Care</span>
              </div>
              <div className="flex items-center space-x-2 text-blue-100">
                <div className="w-2 h-2 bg-cyan-400 rounded-full"></div>
                <span className="text-sm font-medium">Expert Medical Team</span>
              </div>
              <div className="flex items-center space-x-2 text-blue-100">
                <div className="w-2 h-2 bg-teal-400 rounded-full"></div>
                <span className="text-sm font-medium">Modern Facilities</span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;